import Keycloak from 'keycloak-js';
import * as Sentry from '@sentry/react';
import { isTransientAuthError } from './authErrors';
import {
  KEYCLOAK_RECOVERY_LOOP_GUARD,
  KEYCLOAK_RECOVERY_REDIRECT_PENDING,
  buildRedirectUri as buildNeutralRedirectUri,
  cleanCurrentUrl,
  createRecoveryBudget,
  ensureSecureContext as ensureNeutralSecureContext,
} from './browserSession';

/**
 * The url/realm/clientId variant of keycloak-js's `KeycloakConfig` union.
 * Named explicitly because the union's other member (`GenericOidcConfig`) has
 * no `realm`, which would make `describeClient` below unable to compose the
 * issuer.
 */
type DashboardKeycloakConfig = { url: string; realm: string; clientId: string };

const buildConfig = (): DashboardKeycloakConfig => ({
  url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8180',
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'lutiq-dashboard',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lutiq-dashboard-spa',
});

const keycloak = new Keycloak(buildConfig());

type InitPhase = 'idle' | 'pending' | 'success' | 'failed';

let initState: {
  phase: InitPhase;
  promise: Promise<boolean> | null;
} = { phase: 'idle', promise: null };

// Bounded per-tab counter that prevents an infinite recover → reload loop
// when Keycloak itself is broken. Stored in sessionStorage so a reload
// preserves the budget within a single tab but doesn't leak across tabs.
const RECOVERY_STORAGE_KEY = 'lutiq:keycloak:recovery-attempts';
const MAX_RECOVERY_ATTEMPTS = 2;

const recoveryBudget = createRecoveryBudget(
  RECOVERY_STORAGE_KEY,
  MAX_RECOVERY_ATTEMPTS,
  KEYCLOAK_RECOVERY_LOOP_GUARD,
);

/**
 * Provider-neutral: shared verbatim with the Scalekit adapter via
 * `./browserSession`. Re-exported here because `keycloak.ts` is the import
 * site the existing call sites and `keycloak.test.ts` already pin.
 */
export const buildRedirectUri = (path?: string): string => buildNeutralRedirectUri(path);

/**
 * Static description of the OIDC client this adapter presents, for the
 * provider-neutral seam. Derived from `buildConfig()` rather than from the
 * keycloak-js instance because keycloak-js only copies `realm`/`clientId` onto
 * itself during `init()`, and the seam must be able to answer before boot.
 */
export const describeClient = () => {
  const config = buildConfig();
  return {
    clientId: config.clientId,
    // Pinned on `init({ pkceMethod: 'S256' })` below.
    pkceChallengeMethod: 'S256',
    // Keycloak composes the issuer as `<base>/realms/<realm>`.
    issuerFor: (idpBaseUrl: string): string => `${idpBaseUrl}/realms/${config.realm}`,
  };
};

// Test-only helper — lets unit tests reset the module singleton between cases.
// Production code must never call this.
export const __resetForTests = (): void => {
  initState = { phase: 'idle', promise: null };
  recoveryBudget.clear();
};

export const getKeycloak = () => keycloak;

const attemptRecovery = async (): Promise<never> => {
  // Throws `keycloak-recovery-loop-guard-tripped` once the budget is spent,
  // having dropped the counter so the NEXT user-initiated login attempt starts
  // with a fresh budget. AuthContext surfaces the terminal state from there.
  recoveryBudget.consume();

  cleanCurrentUrl();
  initState = { phase: 'idle', promise: null };

  // No `prompt: 'login'` — the user's Keycloak SSO session cookie may still
  // be valid; a PKCE/state mismatch is a client-side problem and doesn't
  // justify forcing a credential re-entry.
  await keycloak.login({ redirectUri: buildRedirectUri('/') });
  // In production keycloak.login() navigates the browser away, so this
  // throw is unreachable. In tests (and as a defensive fallback) the throw
  // tags the promise so AuthContext can suppress the loading→false flicker.
  throw new Error(KEYCLOAK_RECOVERY_REDIRECT_PENDING);
};

// Keycloak-js's createUUID() calls window.crypto.getRandomValues(), which is
// only exposed on secure contexts (https://, localhost, file://). On insecure
// http:// origins the constructor throws `Error: Web Crypto API is not
// available.` from deep inside keycloak-js — LUTIQ-1Q/1P/1N. See
// `ensureSecureContext` in ./browserSession for the full reasoning; it is
// shared with the Scalekit adapter, which has the identical Web Crypto
// dependency through PKCE S256.
const ensureSecureContext = (): void =>
  ensureNeutralSecureContext(KEYCLOAK_RECOVERY_REDIRECT_PENDING);

export const initKeycloak = (): Promise<boolean> => {
  ensureSecureContext();
  if (initState.phase === 'success' || initState.phase === 'pending') {
    return initState.promise!;
  }
  const promise: Promise<boolean> = keycloak
    .init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      redirectUri: buildRedirectUri(),
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      initState = { phase: 'success', promise };
      // Successful login invalidates any earlier recovery attempt budget.
      recoveryBudget.clear();
      return authenticated;
    })
    .catch(async (err) => {
      // A transient network blip during the OAuth code→token exchange (very
      // common on mobile WebKit, which rejects fetch() with "Load failed")
      // isn't actionable — attemptRecovery() below re-drives the flow. Only
      // capture a real, persistent init failure; breadcrumb the blips so they
      // don't open a Sentry issue for every flaky-network mobile user
      // (LUTIQ-60). The classifier is shared with AuthContext's boot path.
      if (isTransientAuthError(err)) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message: 'keycloak-init transient network failure (recovery scheduled)',
        });
      } else {
        Sentry.captureException(err, { tags: { component: 'keycloak-init' } });
      }
      initState = { phase: 'failed', promise };
      return attemptRecovery();
    });
  initState = { phase: 'pending', promise };
  return promise;
};

export const isKeycloakInitialized = (): boolean => initState.phase === 'success';

/**
 * Escape hatch for mid-session recovery — `onAuthError` /
 * `onAuthRefreshError` fire *after* a successful init, so calling
 * `initKeycloak()` from them is a no-op (the state machine short-circuits
 * on `phase === 'success'`). This helper resets the singleton first so the
 * next attempt actually hits the init/catch path and the recovery ladder.
 *
 * Rejects with `keycloak-recovery-redirect-pending` or
 * `keycloak-recovery-loop-guard-tripped` the same way `initKeycloak()`
 * does, so callers can `.catch(() => {})` and let AuthContext surface the
 * outcome through the existing handlers.
 */
export const forceRecovery = async (): Promise<never> => {
  initState = { phase: 'idle', promise: null };
  return attemptRecovery();
};

export const ensureFreshToken = async (): Promise<string | null> => {
  // A cold load triggered by `onTokenExpired` (e.g. coming back to an idle
  // tab) hits this path before `initKeycloak()` is ever called, so the
  // module-entry guard in `initKeycloak` alone isn't enough to fully close
  // LUTIQ-1Q/1P/1N. keycloak-js's silent refresh still calls `createUUID()`
  // on the authorize fallback; mirror the guard here so every path into
  // keycloak-js runs from a secure context.
  ensureSecureContext();
  if (initState.phase !== 'success' || !keycloak.authenticated) {
    return null;
  }

  try {
    await keycloak.updateToken(30);
  } catch {
    return null;
  }

  return keycloak.token ?? null;
};

export const loginWithGoogle = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  const idpHint = import.meta.env.VITE_KEYCLOAK_GOOGLE_IDP_HINT;
  // buildRedirectUri() is OAuth-param-clean even when window.location is
  // contaminated, so callers never need to sanitise up front.
  await keycloak.login({
    redirectUri: buildRedirectUri(redirectPath),
    ...(idpHint ? { idpHint } : {}),
  });
};

export const loginWithPassword = async (redirectPath?: string): Promise<void> => {
  ensureSecureContext();
  await keycloak.login({
    redirectUri: buildRedirectUri(redirectPath),
  });
};

export const logout = async (): Promise<void> => {
  await keycloak.logout({
    redirectUri: buildRedirectUri('/'),
  });
};
